import {useCallback, useEffect, useMemo} from 'react';
import type {Data} from 'schemaDefinition/types';
import {useGetResourceListByIds} from 'api/hooks/useGetResourceListByIds';
import {getOffset} from 'services/utils/getOffset';
import {useColumnConfigState} from 'schema/table/hooks/useColumnConfigState';

export function useDataByIdPagination<TData extends Data = Data>(
  schemaName: string,
  ids: string[],
  loader: (ids: string[]) => Promise<TData[]>,
) {
  const {paginationModel, resetPaginationModel} =
    useColumnConfigState(schemaName);

  useEffect(() => {
    resetPaginationModel();
  }, [resetPaginationModel]);

  const itemIds = useMemo(() => {
    const offset = getOffset(paginationModel.pageSize, paginationModel.page);
    return ids.slice(offset, offset + paginationModel.pageSize);
  }, [ids, paginationModel.page, paginationModel.pageSize]);

  const load = useCallback(
    (pageIds: string[]) => {
      return pageIds.length ? loader(pageIds) : Promise.reject();
    },
    [loader],
  );

  const {data, status} = useGetResourceListByIds<TData>(itemIds, load);

  return {
    data: data ?? [],
    status,
  };
}
